import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import QboxmailApi from "../../../qboxmailApi";
import isFQDN from "validator/lib/isFQDN";

import SupportContactBox from "Components/supportContactBox/SupportContactBox";
import WarningBanner from "Components/warningBanner/WarningBanner";
import HelpTooltip from "Components/helpTooltip/HelpTooltip";
import Input from "Components/input/Input";
import Button from "Components/button/Button";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import TranslatedString from "Components/TranslatedString";
import t from "Utils/translation";
import { less, plus } from "Icons/index";
import cx from "classnames";
import s from "./billingSystem.scss";

const ContextTypes = {
  router: PropTypes.object,
  isMobileDevice: PropTypes.func,
  isMediaMobile: PropTypes.func,
  isMediaSM: PropTypes.func,
  isMediaMD: PropTypes.func,
  isMediaLG: PropTypes.func,
};

const MINIMUM_EMAILS_ORDER = 1;

export default class BillingSystemStep extends React.Component {
  constructor(props) {
    super(props);

    let domains = [];

    // if domains are present in formData, users is coming back from
    // the recap page, so I need to use this data to init this view
    if (props.formData.domains) {
      props.formData.domains.forEach((d) => domains.push(d));
    } else {
      // add exist = true to differentiate the one that are already
      // created in system from the one that the user is adding now

      let doms = undefined;
      if (Object.keys(props.domains).length === 0 && props.domains.constructor === Object) {
        doms = [];
      } else {
        doms = props.domains;
      }

      doms.forEach((d) => {
        let em8gb = d.no_email_accounts - d.no_email_accounts_25gb;

        domains.push({
          ...d,
          exist: true,
          new_plan: d.plan.name,
          new_archive_count: d.no_email_accounts_archive,
          new_8_count: em8gb === 0 ? 1 : em8gb, // if has only postmaster (0), count it
          new_25_count: d.no_email_accounts_25gb,
          new_50_count: d.no_email_accounts_50gb,
          new_100_count: d.no_email_accounts_100gb,
        });
      });
    }

    let newDomainsIDS = 0;
    if (props.formData.domains) {
      if (!domains[domains.length - 1].exist) {
        newDomainsIDS = parseInt(domains[domains.length - 1].code.replace("new_domain_", ""), 10);
      }
    }

    this.state = {
      newDomainsIDS,
      domains,
    };
  }

  validate = () => {
    if (this.props.formData.billing_cycle === "monthly") return true;

    if (!this.state.domains || this.state.domains.length === 0) {
      window.alert("Aggiungi almeno un dominio al tuo ordine.");
      return false;
    }

    let totalEmails = 0;

    let notVerified = false;

    let domainWith0Emails = 0;

    this.state.domains.forEach((d) => {
      let totalDomainEmails = d.new_8_count + d.new_25_count;
      if (!d.exist && !d.valid) notVerified = true;

      totalEmails += d.new_8_count + d.new_25_count;
      if (d.new_plan !== "basic") {
        totalEmails += d.new_50_count;
        totalDomainEmails += d.new_50_count;
      }

      if (d.new_plan === "enterprise") {
        totalEmails += d.new_100_count;
        totalDomainEmails += d.new_100_count;
      }

      if (totalDomainEmails === 0) domainWith0Emails += 1;
    });

    if (domainWith0Emails > 0) {
      window.alert(t("orders.new.alert.noDomainsWithNoEmails"));
      return false;
    }

    if (notVerified) {
      window.alert(t("orders.new.alert.noIncompleteDomains"));
      return false;
    }

    if (totalEmails < MINIMUM_EMAILS_ORDER) {
      window.alert(t("orders.new.alert.noEmptyOrder"));
      return false;
    }

    return true;
  };

  goToNextStep = async () => {
    for (let d of this.state.domains) {
      if (d.exist) continue;
      await this.validateDomain(d);
    }

    // validate
    const isValid = this.validate();
    if (!isValid) return;

    const { prices } = this.props;

    // estimate
    let total = this.props.EURO(0);
    this.state.domains.forEach((d) => {
      total = total.add(this.estimateDomain(d));
    });

    this.props.setFormFieldValue({ domains: this.state.domains, annual_estimate: total.format() });

    this.props.goToStep(6);
  };

  goBack = () => {
    this.props.goToStep(4);
  };

  updateDomain = (code, values) => {
    let domains = [];

    this.state.domains.forEach((d) => {
      if (d.code === code) {
        d = { ...d, ...values };
      }

      domains.push({ ...d });
    });

    this.setState({ domains });
  };

  getDomain = (code) => {
    return this.state.domains.filter((d) => d.code === code)[0];
  };

  validateDomain = async (d) => {
    this.setState({ validatingDomain: true });
    if (!d.name || d.name.length < 3 || !isFQDN(d.name)) {
      this.updateDomain(d.code, { valid: false });
      this.setState({ validatingDomain: false });
    } else {
      const api = new QboxmailApi({ useCookie: true });
      let response;

      try {
        response = await api.validateDomainName(d.name);
      } catch (e) {
        this.updateDomain(d.code, { valid: false });
        this.setState({ validatingDomain: false });
      }

      if (response.valid) {
        this.updateDomain(d.code, { valid: true });
      } else {
        this.updateDomain(d.code, { valid: false });
      }
      this.setState({ validatingDomain: false });
    }

    return true;
  };

  addDomain = async () => {
    const domains = this.state.domains;

    const id = this.state.newDomainsIDS + 1;

    let lastDom = this.state.domains[this.state.domains.length - 1];
    if (lastDom && !lastDom.exist) {
      // if validation has not been triggered, trigger it now
      if (lastDom.valid === undefined) await this.validateDomain(lastDom);
      if (lastDom.valid === false) return;
    }

    domains.push({
      code: `new_domain_${id}`,
      name: "",
      new_plan: "professional",
      plan: {},
      exist: false,
      new_8_count: 1,
      new_25_count: 0,
      new_50_count: 0,
      new_100_count: 0,
      new_archive_count: 0,
      no_email_accounts: 0,
      no_email_accounts_25gb: 0,
      no_email_accounts_archive: 0,
    });

    this.setState({ domains, newDomainsIDS: id });
  };

  estimateDomain = (dom) => {
    const { prices, EURO } = this.props;
    const bc = this.props.formData.billing_cycle;

    let est8 = EURO(prices[bc][`${dom.new_plan}_8`]).multiply(dom.new_8_count);
    let est25 = EURO(prices[bc][`${dom.new_plan}_25`]).multiply(dom.new_25_count);
    let estArchive = EURO(prices[bc][`${dom.new_plan}_vault`]).multiply(dom.new_archive_count);

    let tot = EURO(est8).add(est25).add(estArchive);

    if (dom.new_plan !== "basic") {
      let est50 = EURO(prices[bc][`${dom.new_plan}_50`]).multiply(dom.new_50_count);
      tot = tot.add(est50);
    }

    if (dom.new_plan === "enterprise") {
      let est100 = EURO(prices[bc][`${dom.new_plan}_100`]).multiply(dom.new_100_count);
      tot = tot.add(est100);
    }

    return tot;
  };

  changeDomainPlan = (code, plan) => {
    this.updateDomain(code, { new_plan: plan });
  };

  updateResourceCount = (code, type, count, checkMin = false, validate = true) => {
    let domains = [];

    let actualKey = "no_email_accounts";
    if (type === "archive") actualKey = "no_email_accounts_archive";
    if (type === "25") actualKey = "no_email_accounts_25gb";
    if (type === "50") actualKey = "no_email_accounts_50gb";
    if (type === "100") actualKey = "no_email_accounts_100gb";

    this.state.domains.forEach((d) => {
      const key = `new_${type}_count`;
      if (d.code === code) {
        d[key] = count.length === 0 ? 0 : parseInt(count, 10);
      }

      if (validate) {
        // avoid negative number
        if (d[key] < 0) d[key] = 0;

        // if the domain has already created email accounts, this value
        // is used as a minimum for the order
        let alreadyCreated;
        if (actualKey === "no_email_accounts") {
          alreadyCreated = d[actualKey] - d.no_email_accounts_25gb;
          if (d.new_plan !== "basic") {
            alreadyCreated -= d.no_email_accounts_50gb;
          }
        } else {
          alreadyCreated = d[actualKey];
        }

        if (checkMin && d.exist && d[key] < alreadyCreated) {
          d[key] = alreadyCreated;
        }

        // no domain can't have 0 email accounts because the postmaster is counted in this case
        if (key === "no_email_accounts" && d[key] === 0) {
          d[key] = 1;
        }

        // handle error case, and reset it to 0
        if (isNaN(d[key])) d[key] = 0;

        // cannot have more archive options of the total email accounts
        let totalEmails = d.new_8_count + d.new_25_count;
        if (d.new_archive_count > totalEmails) {
          d.new_archive_count = totalEmails;
        }
      }

      domains.push({ ...d });
    });
    this.setState({ domains });
  };

  handleArrows = (d, type, e) => {
    let count = d[`new_${type}_count`];

    if (e.which !== 38 && e.which !== 40) return;

    if (e.which === 38) count += 1;
    if (e.which === 40) count -= 1;

    this.updateResourceCount(d.code, type, count, true);
  };

  validateResourceCount = (type) => {
    let domains = [];

    let key = "no_email_accounts";
    if (type === "archive") key = "no_email_accounts_archive";
    if (type === "25") key = "no_email_accounts_25gb";
    if (type === "50") key = "no_email_accounts_50gb";
    if (type === "100") key = "no_email_accounts_100gb";

    this.state.domains.forEach((d) => {
      // avoid negative number
      if (d[`new_${type}_count`] < 0) d[`new_${type}_count`] = 0;

      // respect min
      if (d.exist && d[`new_${type}_count`] < d[key]) d[`new_${type}_count`] = d[key];

      domains.push({ ...d });
    });

    this.setState({ domains });
  };

  generateDomainPlanSelector = (d) => {
    return (
      <div className={cx(s.planAnnual)}>
        <div className={s.wrapLabelPlaneAnnual}>
          <label className={s.labelPlaneAnnual} htmlFor={name}>
            {t("input.label.select.plan")}
          </label>

          <div className={s.helpTooltip}>
            <HelpTooltip
              children={[
                {
                  title: t("helpT.order.plan.basic.title"),
                  description: t("helpT.order.plan.basic.desc"),
                },
                {
                  title: t("helpT.order.plan.professional.title"),
                  description: t("helpT.order.plan.professional.desc"),
                },
                {
                  title: t("helpT.order.plan.enterprise.title"),
                  description: t("helpT.order.plan.enterprise.desc"),
                },
              ]}
            />
          </div>
        </div>

        <div className={s.planSelector}>
          <div
            onClick={() => this.changeDomainPlan(d.code, "basic")}
            className={cx({
              [s.selected]: d.new_plan === "basic",
              [s.disabled]: d.exist && d.plan.name !== "basic",
            })}
          >
            {t("plan.basic.name")}
          </div>
          <div
            onClick={() => this.changeDomainPlan(d.code, "professional")}
            className={cx({
              [s.selected]: d.new_plan === "professional",
              [s.disabled]: d.exist && d.plan.name === "enterprise",
            })}
          >
            {t("plan.professional.name")}
          </div>
          <div
            onClick={() => this.changeDomainPlan(d.code, "enterprise")}
            className={cx({
              [s.selected]: d.new_plan === "enterprise",
            })}
          >
            {t("plan.enterprise.name")}
          </div>
        </div>
      </div>
    );
  };

  updateDomainName = (dom, value) => {
    this.updateDomain(dom.code, { name: value, valid: undefined });
  };

  removeDomain = (code) => {
    let domains = [];
    this.state.domains.forEach((d) => {
      if (d.code !== code) {
        domains.push({ ...d });
      }
    });

    this.setState({ domains });
  };

  render() {
    const { prices, formData, formErrors, setFormFieldValue } = this.props;
    const { isMobileDevice, isMediaMobile, isMediaSM, isMediaMD, isMediaLG } = this.context;
    const bc = formData.billing_cycle;

    return (
      <div className="orderPage">
        {this.props.stepper}

        {!isMediaMobile() && !isMediaSM() && !isMediaMD() && <SupportContactBox className="supportBox" />}

        <div className={cx("sectionTitle", "titleFP")}>{t("order.add.title.step5")}</div>

        <div className="wrapScrollFP">
          <div className="infoFP">
            <div className="sectionDesc">{t("order.add.desc.step5.annual")}</div>
          </div>
          <div className="wrapFormsFP">
            <div className="formsFP">
              {this.state.domains.map((d, idx) => {
                return (
                  <div key={d.code} className={cx("inputFull2", s.domainOrderBox)}>
                    <Input
                      className={s.nameDomain}
                      name={d.code}
                      label={t("input.label.domain.name")}
                      value={d.name}
                      onChange={(val) => this.updateDomainName(d, val[d.code])}
                      autocomplete={false}
                      required
                      errors={d.valid === false && t("input.error.notValid.domain")}
                      disabled={d.exist}
                      helpTooltip={
                        <HelpTooltip
                          children={[
                            {
                              description: d.exist ? t("helpT.order.domain.exist") : t("helpT.order.domain.add"),
                            },
                          ]}
                        />
                      }
                    />

                    {this.generateDomainPlanSelector(d)}

                    <div className={s.wrapCounter}>
                      <div className={s.wrapLabelCounter}>
                        <div className={s.labelCounter}>{t("order.counter.emailAccout.8GB")}</div>
                        <div className={s.hintLabelCounter}>
                          {prices[bc][`${d.new_plan}_8`].toFixed(2)} € account / {t("order.counter.price.annual")}
                        </div>
                      </div>
                      {/* 8 gb email counters */}
                      <div className={s.wrapInputCounter}>
                        {bc === "annually" && (
                          <div className={s.resourceCounter}>
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={less}
                              onClick={() => this.updateResourceCount(d.code, "8", d.new_8_count - 1, true)}
                              hoverSmall
                            />
                            <input
                              onKeyDown={(e) => this.handleArrows(d, "8", e)}
                              onBlur={() => this.validateResourceCount("8")}
                              value={d.new_8_count}
                              onChange={(e) =>
                                this.updateResourceCount(d.code, "8", e.currentTarget.value, false, false)
                              }
                            />

                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={plus}
                              onClick={() => this.updateResourceCount(d.code, "8", d.new_8_count + 1, true)}
                              hoverSmall
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={s.wrapCounter}>
                      <div className={s.wrapLabelCounter}>
                        <div className={s.labelCounter}>{t("order.counter.emailAccout.25GB")}</div>
                        <div className={s.hintLabelCounter}>
                          {prices[bc][`${d.new_plan}_25`].toFixed(2)} € account / {t("order.counter.price.annual")}
                        </div>
                      </div>
                      {/* 25 gb email counters */}
                      <div className={s.wrapInputCounter}>
                        {bc === "annually" && (
                          <div className={s.resourceCounter}>
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={less}
                              onClick={() => this.updateResourceCount(d.code, "25", d.new_25_count - 1, true)}
                              hoverSmall
                            />
                            <input
                              onKeyDown={(e) => this.handleArrows(d, "25", e)}
                              onBlur={() => this.validateResourceCount("25")}
                              value={d.new_25_count}
                              onChange={(e) =>
                                this.updateResourceCount(d.code, "25", e.currentTarget.value, false, false)
                              }
                            />
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={plus}
                              onClick={() => this.updateResourceCount(d.code, "25", d.new_25_count + 1, true)}
                              hoverSmall
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {bc === "annually" && d.new_plan !== "basic" && (
                      <div className={s.wrapCounter}>
                        <div className={s.wrapLabelCounter}>
                          <div className={s.labelCounter}>{t("order.counter.emailAccount.50GB")}</div>
                          <div className={s.hintLabelCounter}>
                            {prices[bc][`${d.new_plan}_50`].toFixed(2)} € account / {t("order.counter.price.annual")}
                          </div>
                        </div>
                        {/* 50gb email counters */}
                        <div className={s.wrapInputCounter}>
                          <div className={s.resourceCounter}>
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={less}
                              onClick={() => this.updateResourceCount(d.code, "50", d.new_50_count - 1, true)}
                              hoverSmall
                            />
                            <input
                              onKeyDown={(e) => this.handleArrows(d, "50", e)}
                              onBlur={() => this.validateResourceCount("50")}
                              value={d.new_50_count}
                              onChange={(e) => this.updateResourceCount(d.code, "50", e.currentTarget.value, false)}
                            />
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={plus}
                              onClick={() => this.updateResourceCount(d.code, "50", d.new_50_count + 1, true)}
                              hoverSmall
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {bc === "annually" && d.new_plan === "enterprise" && (
                      <div className={s.wrapCounter}>
                        <div className={s.wrapLabelCounter}>
                          <div className={s.labelCounter}>{t("order.counter.emailAccount.100GB")}</div>
                          <div className={s.hintLabelCounter}>
                            {prices[bc][`${d.new_plan}_100`].toFixed(2)} € account / {t("order.counter.price.annual")}
                          </div>
                        </div>
                        {/* 100gb email counters */}
                        <div className={s.wrapInputCounter}>
                          <div className={s.resourceCounter}>
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={less}
                              onClick={() => this.updateResourceCount(d.code, "100", d.new_100_count - 1, true)}
                              hoverSmall
                            />
                            <input
                              onKeyDown={(e) => this.handleArrows(d, "100", e)}
                              onBlur={() => this.validateResourceCount("100")}
                              value={d.new_100_count}
                              onChange={(e) => this.updateResourceCount(d.code, "100", e.currentTarget.value, false)}
                            />
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={plus}
                              onClick={() => this.updateResourceCount(d.code, "100", d.new_100_count + 1, true)}
                              hoverSmall
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className={s.paidOptions}>
                      <div className={s.labelPaidOptions}>
                        <TranslatedString>order.counter.paidOptions</TranslatedString>
                      </div>
                    </div>

                    <div className={s.wrapCounter}>
                      <div className={s.wrapLabelCounter}>
                        <div className={s.labelCounter}>{t("order.counter.archive")}</div>
                        <div className={s.hintLabelCounter}>
                          {prices[bc][`${d.new_plan}_vault`].toFixed(2)} € account / {t("order.counter.price.annual")}
                        </div>
                      </div>
                      {/* archive email counters */}
                      <div className={s.wrapInputCounter}>
                        {bc === "annually" && (
                          <div className={s.resourceCounter}>
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={less}
                              onClick={() => this.updateResourceCount(d.code, "archive", d.new_archive_count - 1, true)}
                              hoverSmall
                            />
                            <input
                              onKeyDown={(e) => this.handleArrows(d, "archive", e)}
                              onBlur={() => this.validateResourceCount("archive")}
                              value={d.new_archive_count}
                              onChange={(e) =>
                                this.updateResourceCount(d.code, "archive", e.currentTarget.value, false)
                              }
                            />
                            <ButtonIcon
                              className={s.btnResourceCounter}
                              icon={plus}
                              onClick={() => this.updateResourceCount(d.code, "archive", d.new_archive_count + 1, true)}
                              hoverSmall
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={s.estimate}>
                      <div>{t("order.label.first.bill.annual")}</div>
                      <div className={s.totalEstimate}>{this.estimateDomain(d).format()} €</div>
                    </div>

                    <div className={s.infoEstimate}>
                      <TranslatedString>order.fee.priceVat</TranslatedString>
                    </div>

                    {!d.exist && (
                      <div className={s.deleteDomainBtn} onClick={() => this.removeDomain(d.code)}>
                        {t("button.delete.domain")}
                      </div>
                    )}
                  </div>
                );
              })}

              {bc === "annually" && (
                <div className={cx("inputFull2", s.wrapAddDomainBtn)}>
                  {this.state.validatingDomain ? (
                    <div className={s.infoAddDomina}>{t("infoMsg.validation.domain.inPorgress")}</div>
                  ) : (
                    <Button className={"w-full"} color="outline" onClick={this.addDomain}>
                      button.addDomainFull
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="footerFP">
              <Button color="text" type="button" onClick={this.props.backToList}>
                button.cancel
              </Button>
              <Button color="accent" onClick={this.goToNextStep}>
                button.continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

BillingSystemStep.contextTypes = ContextTypes;
